<template>
    <div>
        <div class="navigation">
            <div
                class="main-nav"
                :class="{'is-demo': isDemo}"
            >
                <router-link
                    to="/"
                    class="desktop-only"
                >
                    <img
                        src="/flux_logo_only.svg"
                    >
                    <template v-if="isDemo">
                        <span class="demo-title">
                            Public Demo
                        </span>
                    </template>
                </router-link>
                <router-link
                    to="/"
                    class="mobile-only"
                >
                    <img
                        src="/flux_logo.svg"
                        class="logo-image"
                    >
                    <template v-if="isDemo">
                        <span class="demo-title">
                            Public Demo
                        </span>
                    </template>
                </router-link>
                <router-link
                    id="home"
                    to="/"
                    class="desktop-only desktop-link"
                    :class="route.path === '/' ? 'active-link' : ''"
                >
                    <q-icon
                        name="home"
                        size="md"
                    />
                    Home
                </router-link>
                <router-link
                    id="reports"
                    to="/reports"
                    class="desktop-only desktop-link"
                    :class="route.path === '/reports' ? 'active-link' : ''"
                >
                    <q-icon
                        name="assignment"
                        size="md"
                    />
                    Reports
                </router-link>
                <router-link
                    id="history"
                    to="/history"
                    class="desktop-only desktop-link"
                    :class="route.path === '/history' ? 'active-link' : ''"
                >
                    <q-icon
                        name="history"
                        size="md"
                    />
                    History
                </router-link>
                <router-link
                    id="settings"
                    to="/settings"
                    class="desktop-only desktop-link"
                    :class="route.path === '/settings' ? 'active-link' : ''"
                >
                    <q-icon
                        name="settings"
                        size="md"
                    />
                    Settings
                </router-link>
                <!-- filler -->
                <q-space />
            </div>
            <div
                id="user-menu"
            >
                <q-icon
                    name="mdi-menu"
                    size="md"
                    class="mobile-only"
                />
                <div
                    id="gravitar-image-container"
                    class="desktop-only"
                >
                    <img
                        :src="avatarUrl"
                        class="gravitar"
                    >
                </div>

                <q-menu
                    ref="navMenu"
                    :offset="[0, 10]"
                    class="navigation-menu"
                >
                    <q-list>
                        <q-item>
                            <div class="flex">
                                <img
                                    :src="avatarUrl"
                                    class="gravitar"
                                >
                                <div class="q-ml-sm">
                                    <p class="q-mb-none">{{ email }}</p>
                                </div>
                            </div>
                        </q-item>
                        <q-item
                            class="mobile-only"
                            clickable
                            to="/"
                        >
                            <p
                                :class="route.path === '/' ? 'active-link' : ''"
                            >
                                Home
                            </p>
                        </q-item>
                        <q-item
                            class="mobile-only"
                            clickable
                            to="/reports"
                        >
                            <p
                                :class="route.path === '/reports' ? 'active-link' : ''"
                            >
                                Reports
                            </p>
                        </q-item>
                        <!-- Flux-3267: hide the history page for now -->
                        <q-item
                            v-if="false"
                            class="mobile-only"
                            clickable
                            to="/history"
                        >
                            <p
                                :class="route.path === '/history' ? 'active-link' : ''"
                            >
                                History
                            </p>
                        </q-item>
                        <q-item
                            class="mobile-only"
                            clickable
                            to="/settings"
                        >
                            <p
                                :class="route.path === '/settings' ? 'active-link' : ''"
                            >
                                Settings
                            </p>
                        </q-item>
                        <q-item
                            clickable
                            to="/methodology"
                        >
                            Methodology
                        </q-item>
                        <q-item
                            clickable
                            class="desktop-only"
                            @click="beginTour()"
                        >
                            Tour
                        </q-item>
                        <q-item
                            v-if="!isDemo"
                            clickable
                            href="https://foundryspatial.atlassian.net/servicedesk/customer/portals"
                            target="_blank"
                        >
                            Help Center
                        </q-item>
                        <q-item
                            clickable
                            @click="emit('logout')"
                        >
                            Log Out
                        </q-item>
                        <q-item
                            clickable
                            :href="!isDemo ? 'https://www.fluxforecast.com/terms-of-service' : 'https://www.fluxforecast.com/demo-terms'"
                            target="_blank"
                        >
                            <span
                                class="terms-link"
                            >
                                Terms of Service & Privacy Policy
                            </span>
                        </q-item>
                    </q-list>
                </q-menu>
            </div>
        </div>
        <FluxTour
            v-if="showTour"
            @end-tour="showTour = false"
        />
    </div>
</template>

<script setup>
import FluxTour from './FluxTour.vue';
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { isDemoUser, LOCALSTORAGE_USER_KEY } from '@/utils/auth';

const emit = defineEmits(['logout']);

const isDemo = ref(false);
const route = useRoute();
const avatarUrl = ref('');
const email = ref('');
const navMenu = ref();

onMounted(async () => {
    if (isDemoUser()) {
        // might need to make this a computed property
        isDemo.value = true;
    }
    avatarUrl.value = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER_KEY)).avatarUrl;
    email.value = JSON.parse(localStorage.getItem(LOCALSTORAGE_USER_KEY)).name;
});

const showTour = ref(false);

const beginTour = () => {
    showTour.value = true;
    navMenu.value.hide();
};

</script>

<style scoped lang="scss">
.navigation {
    width: 65px;
    position: absolute;
    height: 100vh;
    background-color: white;
    z-index: 100;
    display: grid;
    grid-template-rows: auto 70px;

    .main-nav {
        display: grid;
        grid-template-rows: repeat(4, 65px) auto;
        text-align: center;
        font-size: 0.9rem;
        gap: 4px;

        .desktop-link {
            align-content: center;
            margin: 0 6px 0 6px;
            width: 53px;
            height: 53px;
        }

        &.is-demo {
            grid-template-rows: 90px repeat(4, 65px) auto;
        }

        span.demo-title {
            display: inline-block;
            line-height: 1rem;

            @media screen and (max-width: $mobile-breakpoint) {
                font-size: 0.8em;
                margin-left: 0.4em;
                margin-top: 0.2em;
            }
        }

        img {
            width: 40px;
            height: 40px;
            margin: 5px;
        }
        a {
            text-decoration: none;
            padding: 4px;
            border-radius: 5px;
            &.active-link {
                background-color: rgba(0,0,0,0.1);
                font-weight: bold;
            }
        }
        a:hover {
            @media screen and (min-width: $mobile-breakpoint) {
                transition: background-color 0.3s cubic-bezier(0.25, 0.8, 0.5, 1), opacity 0.6s cubic-bezier(0.25, 0.8, 0.5, 1);
                background-color: rgba(0,0,0,0.1);
            }
        }
    }
    #user-menu {
        text-align: center;
        text-decoration: none;
        padding: 4px;

        &.active-link {
            background-color: rgba(0,0,0,0.1);
            font-weight: bold;
        }
    }
}

#gravitar-image-container {
    display: flex;
    cursor: pointer;
    justify-content: center;
    align-items: center;
    height: 100%;
}

.gravitar {
    border-radius: 50%;
    width: 40px;
}
.navigation-menu {
    a {
        text-decoration: none;
    }
    .terms-link {
        font-size: 0.8rem;

        &:hover {
            text-decoration: underline !important;
        }
    }
}
@media screen and (max-width: $mobile-breakpoint) {
    .navigation {
        align-items: center;
        background-color: white;
        display: flex;
        height: $nav-height;
        justify-content: space-between;
        align-items: center;
        width: 100vw;

        .main-nav {
            display: flex;
            width: 100%;
            img {
                width: inherit;
                padding-top: 0.2rem;
            }

        }

        span.demo-title {
            display: inline-block;
            position: absolute;
            top: 40px;
            left: 58px;
        }

        div {
            display: flex;
            align-items: center;

            a {
                margin: 0 1rem;
                font-size: 1.2rem;
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }

            .active-link {
                color: $primary;
                font-weight: bold;
            }
        }
    }
}
</style>
